/**
 * Utilities for parsing and normalizing PI tag names according to ISA standards.
 */
import type { MatchedDatapoint } from 'features/timeSeries'

/**
 * Display label for a linked datastream. Fleet-template historians carry one
 * point per unit under the SAME name (`SCA0_1TE7092BPV` × 12 gensets), so the
 * `point_source` (Genset_7, BOP, …) is the only disambiguator — always shown
 * when present.
 */
export function datapointDisplayLabel(datapoint: {
  pi_tag?: string
  name?: string
  point_source?: string
}): string {
  const base = datapoint.pi_tag || datapoint.name || ''
  return datapoint.point_source ? `${base} · ${datapoint.point_source}` : base
}

/** BOP prefix pattern: BOP1., BOP2., BOP3., etc. */
const BOP_PREFIX_PATTERN = /^BOP\d+\./i

/**
 * ISA instrument type suffixes that often measure the same thing:
 * - PT/PI/PE (Pressure Transmitter/Indicator/Element)
 * - TT/TI/TE (Temperature)
 * - FT/FI/FE (Flow)
 * - LT/LI/LE (Level)
 * - AT/AI/AE/AIT (Analyzer)
 */
const ISA_TYPE_PATTERN = /^(\d+-)([A-Z])([TIEAC])([-\w]+)$/i

/**
 * Extracts the core tag name by removing BOP prefix and normalizing ISA type suffix.
 * @example "BOP1.01-PT-RWS101A" -> "01-P-RWS101A"
 * @example "01-PI-RWS101A" -> "01-P-RWS101A"
 */
export function getNormalizedCoreName(piTag: string): string {
  let core = piTag.replace(BOP_PREFIX_PATTERN, '')

  const match = core.match(ISA_TYPE_PATTERN)
  if (match) {
    const [, prefix, firstLetter, , suffix] = match
    core = `${prefix}${firstLetter}-${suffix}`
  }

  return core.toUpperCase()
}

/**
 * Checks if a tag has a BOP prefix.
 */
export function hasBopPrefix(piTag: string): boolean {
  return BOP_PREFIX_PATTERN.test(piTag)
}

/**
 * Checks if a tag has a "Transmitter" type (T suffix), which is preferred over Indicators (I) or Elements (E).
 */
export function isTransmitterType(piTag: string): boolean {
  return /^(\d+-)?[A-Z]T[-\d]/i.test(piTag.replace(BOP_PREFIX_PATTERN, ''))
}

export function dedupeById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>()
  const result: T[] = []
  for (const item of items) {
    if (seen.has(item.id)) continue
    seen.add(item.id)
    result.push(item)
  }
  return result
}

/**
 * Tag-linked datapoints are exact — never ISA-collapse or drop them. They come
 * first, taken as-is; text matches follow, minus any id a tag link already
 * covers.
 */
export function mergeTagLinkedFirst<T extends { id: string }>(
  tagLinked: T[],
  textMatched: T[]
): T[] {
  if (tagLinked.length === 0) return textMatched
  const tagLinkedIds = new Set(tagLinked.map((datapoint) => datapoint.id))
  return [
    ...tagLinked,
    ...textMatched.filter((datapoint) => !tagLinkedIds.has(datapoint.id)),
  ]
}

/**
 * All datastreams linked to a subject. The subject's tag-linked datapoint ids
 * (resolved via `getDatapointsByIds`) are authoritative and come first via
 * {@link mergeTagLinkedFirst}; text matches on the name and every alias
 * follow, deduplicated via {@link deduplicateDatapoints}.
 */
export function matchSubjectDatapoints<T extends MatchedDatapoint>(
  subject:
    | {
        name: string
        aliases: Array<{ value: string; kind?: string }>
        datapoints?: string[]
      }
    | undefined,
  getMatchedDatapoints: (text: string | undefined) => T[],
  getDatapointsByIds?: (ids: readonly string[]) => T[]
): T[] {
  if (!subject) return []
  const texts = subject.aliases
    .filter((alias) => alias.kind === 'canonical_tag')
    .map((alias) => alias.value)
    .filter((text) => !!text)
  const textMatched = deduplicateDatapoints(
    dedupeById(texts.flatMap((text) => getMatchedDatapoints(text)))
  )
  const tagLinked = getDatapointsByIds?.(subject.datapoints ?? []) ?? []
  return mergeTagLinkedFirst(tagLinked, textMatched)
}

/**
 * Deduplicates datapoints, preferring:
 * 1. Clean tags over BOP-prefixed ones
 * 2. Transmitter types (PT, TT) over Indicator/Element types (PI, TI, TE)
 */
export function deduplicateDatapoints<T extends MatchedDatapoint>(
  datapoints: T[]
): T[] {
  const byCoreName = new Map<string, T[]>()

  for (const dp of datapoints) {
    const coreName = getNormalizedCoreName(dp.pi_tag)
    const existing = byCoreName.get(coreName) || []
    existing.push(dp)
    byCoreName.set(coreName, existing)
  }

  const result: T[] = []
  for (const group of byCoreName.values()) {
    if (group.length === 1) {
      result.push(group[0])
      continue
    }

    const cleanTransmitter = group.find(
      (dp) => !hasBopPrefix(dp.pi_tag) && isTransmitterType(dp.pi_tag)
    )
    if (cleanTransmitter) {
      result.push(cleanTransmitter)
      continue
    }

    const cleanOther = group.find((dp) => !hasBopPrefix(dp.pi_tag))
    if (cleanOther) {
      result.push(cleanOther)
      continue
    }

    const bopTransmitter = group.find((dp) => isTransmitterType(dp.pi_tag))
    if (bopTransmitter) {
      result.push(bopTransmitter)
      continue
    }

    result.push(group[0])
  }

  return result
}
